import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CalculateGrowthRate = ({ data }) => {
  const pdfRef = useRef();
  const safeData = data ?? [];

  if (!safeData.length) {
    return <p>No data available</p>;
  }

  // =========================
  // Summary Calculations
  // =========================
  const latestYear = safeData[safeData.length - 1].year;

  const totalEmployees = safeData.reduce(
    (sum, row) => sum + (parseInt(row.num_employees) || 0),
    0,
  );

  const highestGrowth = safeData.reduce(
    (max, row) =>
      parseFloat(row.growth_rate || 0) > parseFloat(max.growth_rate || 0)
        ? row
        : max,
    safeData[0],
  );

  // =========================
  // Chart Data
  // =========================
  const chartData = {
    labels: safeData.map((item) => item.year),
    datasets: [
      {
        type: "bar",
        label: "Number of Employees",
        data: safeData.map((item) => parseInt(item.num_employees || 0)),
        backgroundColor: "#014CB2",
        borderRadius: 6,
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Growth Rate (%)",
        data: safeData.map((item) =>
          item.growth_rate !== null ? parseFloat(item.growth_rate) : null,
        ),
        borderColor: "#ef4444",
        backgroundColor: "#ef4444",
        tension: 0.3,
        borderWidth: 2,
        yAxisID: "y2",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employees & Growth Rate (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label === "Growth Rate (%)") {
              return `${context.parsed.y}%`;
            }
            return context.parsed.y.toLocaleString();
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employees",
        },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
      y2: {
        position: "right",
        beginAtZero: true,
        title: {
          display: true,
          text: "Growth Rate (%)",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text(APP_VERSION, 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("growth_rate_analysis.pdf");
   });
 };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Growth Rate Analysis
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Latest Year</p>
            <p className="text-xl font-bold text-blue-800">{latestYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employees (All Years)
            </p>
            <p className="text-xl font-bold text-green-800">
              {totalEmployees.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Growth</p>
            <p className="text-xl font-bold text-yellow-800">
              {highestGrowth.year} ({highestGrowth.growth_rate}%)
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Employees</th>
                  <th className="p-2">Growth (%)</th>
                </tr>
              </thead>
              <tbody>
                {safeData.map((row) => (
                  <tr key={row.year} className="hover:bg-gray-100">
                    <td className="p-2">{row.year}</td>
                    <td className="p-2">
                      {Number(row.num_employees || 0).toLocaleString()}
                    </td>
                    <td className="p-2">
                      {row.growth_rate !== null ? `${row.growth_rate}%` : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-96">
            <Chart type="bar" data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf" />
        </button>
      </div>
    </>
  );
};

export default CalculateGrowthRate;

